<script setup>
import { ref, computed } from 'vue';
import { Card } from 'primevue';
import InputNumber from 'primevue/inputnumber';
import ANButton from '@/components/common/AnButton.vue';
import Chart from 'primevue/chart';
import { calculateSWP, generateSWPProjection } from '@/utils/financeCalculators.js';

// Form data
const formData = ref({
  swp: null,
  corpus: 1000000,
  durationYears: 20,
  rate: 12
});

const calculationMode = ref('calculateCorpus'); // 'calculateCorpus' or 'calculateSWP'
const result = ref(null);
const chartData = ref({});
const chartOptions = ref({});

// Computed properties
const isValidInput = computed(() => {
  if (calculationMode.value === 'calculateCorpus') {
    return formData.value.swp > 0 && formData.value.durationYears > 0 && formData.value.rate > 0;
  } else {
    return formData.value.corpus > 0 && formData.value.durationYears > 0 && formData.value.rate > 0;
  }
});

// Calculate SWP
const calculate = () => {
  if (!isValidInput.value) return;

  const input = {
    swp: calculationMode.value === 'calculateCorpus' ? formData.value.swp : null,
    corpus: calculationMode.value === 'calculateSWP' ? formData.value.corpus : null,
    durationYears: formData.value.durationYears,
    rate: formData.value.rate
  };

  result.value = calculateSWP(input);
  if (!result.value.error) {
    generateChart();
    // Scroll to results section after calculation
    setTimeout(() => {
      const resultsElement = document.querySelector('.result-card');
      if (resultsElement) {
        resultsElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest' 
        });
      }
    }, 100);
  }
};

// Generate chart data for SWP projection
const generateChart = () => {
  const swpAmount = calculationMode.value === 'calculateCorpus' ? 
    formData.value.swp : result.value.swpAmount;
  const initialCorpus = calculationMode.value === 'calculateSWP' ? 
    formData.value.corpus : result.value.corpusRequired;

  const projectionData = generateSWPProjection({
    corpus: initialCorpus,
    swp: swpAmount,
    durationYears: formData.value.durationYears,
    rate: formData.value.rate
  });

  const years = projectionData.map(d => `Year ${d.year}`);
  const remainingCorpus = projectionData.map(d => d.remainingCorpus);
  const totalWithdrawn = projectionData.map(d => d.totalWithdrawn);

  chartData.value = {
    labels: years,
    datasets: [
      {
        label: 'Remaining Corpus',
        data: remainingCorpus,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        fill: false,
        yAxisID: 'y'
      },
      {
        label: 'Total Withdrawn',
        data: totalWithdrawn,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 2,
        fill: false,
        yAxisID: 'y1'
      }
    ]
  };

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'SWP Projection: Corpus Depletion vs Withdrawals'
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Years'
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Remaining Corpus (₹)'
        },
        ticks: {
          callback: function(value) {
            return '₹' + (value / 100000).toFixed(1) + 'L';
          }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Total Withdrawn (₹)'
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: function(value) {
            return '₹' + (value / 100000).toFixed(1) + 'L';
          }
        }
      }
    }
  };
};

// Watch for mode change and clear results
import { watch } from 'vue';
watch(calculationMode, () => {
  result.value = null;
  chartData.value = {};
});

// Initialize
calculate();
</script>

<template>
  <div class="swp-calculator">
    <div class="calculator-grid">
      <!-- Input Form -->
      <Card class="input-card">
        <template #title>
          <div class="card-header">
            <h3>SWP Calculator Input</h3>
            <div class="mode-toggle">
              <button 
                :class="['mode-btn', { active: calculationMode === 'calculateCorpus' }]"
                @click="calculationMode = 'calculateCorpus'"
              >
                Calculate Required Corpus
              </button>
              <button 
                :class="['mode-btn', { active: calculationMode === 'calculateSWP' }]"
                @click="calculationMode = 'calculateSWP'"
              >
                Calculate Monthly SWP
              </button>
            </div>
          </div>
        </template>
        <template #content>
          <div class="form-fields">
            <!-- SWP Amount (for Corpus calculation) -->
            <div v-if="calculationMode === 'calculateCorpus'" class="field">
              <label for="swp">Monthly Withdrawal Amount (₹)</label>
              <InputNumber
                id="swp"
                v-model="formData.swp"
                :min="1000"
                :max="1000000"
                :step="1000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">Amount you want to withdraw monthly</small>
            </div>

            <!-- Corpus (for SWP calculation) -->
            <div v-if="calculationMode === 'calculateSWP'" class="field">
              <label for="corpus">Available Corpus (₹)</label>
              <InputNumber
                id="corpus"
                v-model="formData.corpus"
                :min="100000"
                :max="100000000"
                :step="100000"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">Your current investment corpus</small>
            </div>

            <!-- Duration -->
            <div class="field">
              <label for="duration">Withdrawal Duration (Years)</label>
              <InputNumber
                id="duration"
                v-model="formData.durationYears"
                :min="1"
                :max="50"
                :step="1"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">How long you want withdrawals to last</small>
            </div>

            <!-- Rate -->
            <div class="field">
              <label for="rate">Expected Annual Return (%)</label>
              <InputNumber
                id="rate"
                v-model="formData.rate"
                :min="1"
                :max="30"
                :step="0.5"
                :minFractionDigits="1"
                :maxFractionDigits="2"
                showButtons
                buttonLayout="horizontal"
                decrementButtonClass="p-button-secondary"
                incrementButtonClass="p-button-secondary"
                class="input-field"
              />
              <small class="help-text">Expected return from your investments</small>
            </div>

            <!-- Calculate Button -->
            <ANButton
              label="Calculate"
              severity="primary"
              size="large"
              @click="calculate"
              :disabled="!isValidInput"
              class="calculate-btn"
            />
          </div>
        </template>
      </Card>

      <!-- Results -->
      <Card v-if="result && !result.error" class="result-card">
        <template #title>
          <h3>SWP Calculation Results</h3>
        </template>
        <template #content>
          <div class="results">
            <div v-if="calculationMode === 'calculateCorpus'" class="result-item highlight">
              <div class="result-label">Required Corpus</div>
              <div class="result-value">₹{{ result.corpusRequired.toLocaleString('en-IN') }}</div>
            </div>

            <div v-if="calculationMode === 'calculateSWP'" class="result-item highlight">
              <div class="result-label">Monthly SWP Amount</div>
              <div class="result-value">₹{{ result.swpAmount.toLocaleString('en-IN') }}</div>
            </div>

            <!-- Summary Information -->
            <div class="result-summary">
              <div class="summary-item">
                <span class="summary-label">Total Withdrawal Period:</span>
                <span class="summary-value">{{ formData.durationYears }} years</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Total Amount Withdrawn:</span>
                <span class="summary-value">
                  ₹{{ ((calculationMode === 'calculateCorpus' ? formData.swp : result.swpAmount) * formData.durationYears * 12).toLocaleString('en-IN') }}
                </span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Monthly Income:</span>
                <span class="summary-value income">
                  ₹{{ (calculationMode === 'calculateCorpus' ? formData.swp : result.swpAmount).toLocaleString('en-IN') }}/month
                </span>
              </div>
            </div>

            <!-- Risk Warning -->
            <div class="risk-warning">
              <div class="warning-icon">⚠️</div>
              <div class="warning-text">
                <strong>Important:</strong> SWP returns depend on market performance. 
                Actual corpus depletion may vary based on market conditions.
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Chart -->
    <Card v-if="result && !result.error && chartData.labels" class="chart-card">
      <template #title>
        <h3>SWP Projection Over Time</h3>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="line" :data="chartData" :options="chartOptions" class="chart" />
        </div>
        <div class="chart-legend">
          <div class="legend-item">
            <div class="legend-color" style="background: rgba(54, 162, 235, 1);"></div>
            <span>Remaining Corpus (Left Axis)</span>
          </div>
          <div class="legend-item">
            <div class="legend-color" style="background: rgba(255, 99, 132, 1);"></div>
            <span>Cumulative Withdrawals (Right Axis)</span>
          </div>
        </div>
      </template>
    </Card>

    <!-- SWP Schedule Table -->
    <Card v-if="result && !result.error" class="schedule-card">
      <template #title>
        <h3>5-Year SWP Schedule Preview</h3>
      </template>
      <template #content>
        <div class="schedule-table">
          <div class="table-header">
            <span>Year</span>
            <span>Starting Balance</span>
            <span>Annual Withdrawal</span>
            <span>Interest Earned</span>
            <span>Ending Balance</span>
          </div>
          <div 
            v-for="year in Math.min(5, formData.durationYears)" 
            :key="year"
            class="table-row"
          >
            <span>{{ year }}</span>
            <span>₹{{ (() => {
              const swpAmount = calculationMode === 'calculateCorpus' ? formData.swp : result.swpAmount;
              const initialCorpus = calculationMode === 'calculateSWP' ? formData.corpus : result.corpusRequired;
              const monthlyRate = formData.rate / 100 / 12;
              let balance = initialCorpus;
              
              for (let y = 1; y < year; y++) {
                for (let month = 1; month <= 12; month++) {
                  balance = balance * (1 + monthlyRate) - swpAmount;
                }
              }
              return Math.round(balance).toLocaleString('en-IN');
            })() }}</span>
            <span>₹{{ ((calculationMode === 'calculateCorpus' ? formData.swp : result.swpAmount) * 12).toLocaleString('en-IN') }}</span>
            <span>₹{{ (() => {
              const swpAmount = calculationMode === 'calculateCorpus' ? formData.swp : result.swpAmount;
              const initialCorpus = calculationMode === 'calculateSWP' ? formData.corpus : result.corpusRequired;
              const monthlyRate = formData.rate / 100 / 12;
              let balance = initialCorpus;
              
              for (let y = 1; y < year; y++) {
                for (let month = 1; month <= 12; month++) {
                  balance = balance * (1 + monthlyRate) - swpAmount;
                }
              }
              
              const yearlyInterest = balance * (formData.rate / 100) - (swpAmount * 12);
              return Math.round(Math.max(0, yearlyInterest)).toLocaleString('en-IN');
            })() }}</span>
            <span>₹{{ (() => {
              const swpAmount = calculationMode === 'calculateCorpus' ? formData.swp : result.swpAmount;
              const initialCorpus = calculationMode === 'calculateSWP' ? formData.corpus : result.corpusRequired;
              const monthlyRate = formData.rate / 100 / 12;
              let balance = initialCorpus;
              
              for (let y = 1; y <= year; y++) {
                for (let month = 1; month <= 12; month++) {
                  balance = balance * (1 + monthlyRate) - swpAmount;
                }
              }
              return Math.round(Math.max(0, balance)).toLocaleString('en-IN');
            })() }}</span>
          </div>
        </div>
      </template>
    </Card>

    <!-- Error Display -->
    <Card v-if="result && result.error" class="error-card">
      <template #content>
        <div class="error-message">
          <i class="pi pi-exclamation-triangle"></i>
          <span>{{ result.error }}</span>
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.swp-calculator {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.calculator-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.card-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-header h3 {
  margin: 0;
  color: var(--an-text-primary);
}

.mode-toggle {
  display: flex;
  gap: 8px;
}

.mode-btn {
  padding: 8px 16px;
  border: 2px solid var(--an-primary);
  background: var(--an-white);
  color: var(--an-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
}

.mode-btn.active {
  background: var(--an-primary);
  color: var(--an-white);
}

.mode-btn:hover:not(.active) {
  background: var(--an-background-light);
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: var(--an-text-primary);
  font-size: 14px;
}

.help-text {
  color: var(--an-text-secondary);
  font-size: 12px;
  font-style: italic;
}

.input-field {
  width: 100%;
}

.calculate-btn {
  width: 100%;
  margin-top: 16px;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-item {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background: var(--an-background-light);
}

.result-item.highlight {
  background: linear-gradient(135deg, var(--an-primary), var(--an-secondary));
  color: var(--an-white);
}

.result-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  opacity: 0.9;
}

.result-value {
  font-size: 2rem;
  font-weight: 700;
}

.result-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--an-gray3);
}

.summary-label {
  color: var(--an-text-secondary);
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: var(--an-text-primary);
}

.summary-value.income {
  color: var(--an-success);
}

.risk-warning {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: var(--an-warning-light);
  border: 1px solid var(--an-warning);
  border-radius: 8px;
  font-size: 14px;
}

.warning-icon {
  font-size: 18px;
}

.warning-text {
  color: var(--an-text-primary);
  line-height: 1.4;
}

.chart-container {
  height: 400px;
  width: 100%;
}

.chart {
  height: 100% !important;
  width: 100% !important;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 16px;
  height: 3px;
  border-radius: 2px;
}

.schedule-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-x: auto;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 60px 140px 140px 140px 140px;
  gap: 16px;
  padding: 12px;
  align-items: center;
  min-width: 600px;
}

.table-header {
  font-weight: 700;
  background: var(--an-background-light);
  border-radius: 6px;
  color: var(--an-text-primary);
  position: sticky;
  top: 0;
}

.table-row {
  border-bottom: 1px solid var(--an-gray3);
  font-size: 14px;
}

.table-row:hover {
  background: var(--an-background-light);
}

.error-card {
  background: var(--an-error-light);
  border: 1px solid var(--an-error);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--an-error);
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .calculator-grid {
    grid-template-columns: 1fr;
  }
  
  .mode-toggle {
    flex-direction: column;
  }
  
  .mode-btn {
    text-align: center;
  }
  
  .result-value {
    font-size: 1.5rem;
  }
  
  .chart-container {
    height: 300px;
  }
  
  .chart-legend {
    flex-direction: column;
    gap: 12px;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 50px 120px 120px 120px 120px;
    gap: 8px;
    font-size: 12px;
  }
}
</style>
